/**
 * overBodySpec — VQE-local structured model for the body of a window-function
 * `OVER (…)` clause (the text between the parentheses). Powers the structured
 * PARTITION BY / ORDER BY / frame editor (`WindowSpecEditor`) that replaces the
 * opaque OVER textarea (Task §4b-i — ANA-05/07/09/14/16/18/20/22).
 *
 * Pure, string-union (no enum), no React. Split across three files to stay
 * under the ~200-line budget:
 *   - this file: the type contract + `serializeOverBody` re-export barrel
 *   - `overBodyParse.ts`: `parseOverBody`
 *   - `overBodySerialize.ts`: `serializeOverBody`
 *
 * HARD round-trip invariant (enforced inside `parseOverBody` via a self-check):
 *   serializeOverBody(parseOverBody(x)) is SEMANTICALLY equal to x for every
 *   real-world OVER body. A body the parser can't confidently structure is
 *   flagged `parseable: false` and its `raw` bytes are preserved verbatim, so a
 *   corrupt/non-standard OVER body NEVER gets silently rewritten — the editor
 *   falls back to the raw textarea instead.
 */
import type { ParsedOrderItem } from './parseSelectItem';

export type { ParsedOrderItem } from './parseSelectItem';

/** Frame unit keyword (lowercase string union — no TS enum). */
export type FrameUnit = 'rows' | 'range' | 'groups';

/** The five standard frame-bound kinds. `n` is the numeric offset for the
 *  `n-preceding` / `n-following` kinds and is absent for the others. */
export type FrameBoundKind =
  | 'unbounded-preceding'
  | 'n-preceding'
  | 'current-row'
  | 'n-following'
  | 'unbounded-following';

export interface FrameBound {
  readonly kind: FrameBoundKind;
  readonly n?: number;
}

export interface FrameSpec {
  readonly unit: FrameUnit;
  readonly start: FrameBound;
  /** `null` for a single-bound frame (`ROWS 5 PRECEDING`) — means the implicit
   *  CURRENT ROW end. serialize preserves this (never invents `AND CURRENT ROW`). */
  readonly end: FrameBound | null;
}

export interface OverBodySpec {
  readonly partitionBy: readonly string[];
  readonly orderBy: readonly ParsedOrderItem[];
  readonly frame: FrameSpec | null;
  /** Trailing `EXCLUDE …` clause, preserved verbatim (never structurally
   *  edited here — out of scope, but must round-trip). Includes the keyword,
   *  e.g. `'EXCLUDE CURRENT ROW'`. */
  readonly frameExclusion: string | null;
  /**
   * `false` when the body couldn't be confidently structured — the editor
   * falls back to the raw textarea and commits `raw` unchanged, so a
   * non-standard body is never silently rewritten.
   */
  readonly parseable: boolean;
  /** The original OVER body text, verbatim. Used for the fallback textarea and
   *  for an exact byte round-trip when `parseable` is `false`. */
  readonly raw: string;
}

/**
 * Frame-bound total order (earliest → latest):
 *   UNBOUNDED PRECEDING  <  N PRECEDING (larger N = earlier)  <  CURRENT ROW
 *   <  N FOLLOWING (larger N = later)  <  UNBOUNDED FOLLOWING.
 * A `BETWEEN start AND end` frame is valid — accepted by every engine — iff
 * `frameBoundRank(end) >= frameBoundRank(start)`. Every N-PRECEDING rank sits
 * strictly below CURRENT ROW and every N-FOLLOWING rank sits strictly above
 * it, regardless of offset — `0 PRECEDING`/`0 FOLLOWING` are legal SQL but
 * stay on their own side of CURRENT ROW rather than collapsing onto it, so
 * e.g. `BETWEEN CURRENT ROW AND 0 PRECEDING` still ranks invalid.
 *
 * Pure — used by `WindowFramePicker` (via `windowFrameOptions.ts`) to keep an
 * ordering-invalid frame unreachable through the guided picker, and unit-
 * tested directly in `overBodySpec.test.ts`.
 */
export function frameBoundRank(bound: FrameBound): number {
  switch (bound.kind) {
    case 'unbounded-preceding':
      return Number.NEGATIVE_INFINITY;
    case 'n-preceding':
      return -((bound.n ?? 0) + 1);
    case 'current-row':
      return 0;
    case 'n-following':
      return (bound.n ?? 0) + 1;
    case 'unbounded-following':
      return Number.POSITIVE_INFINITY;
  }
}

/** True iff `BETWEEN start AND end` is a non-inverted, all-engine-valid frame. */
export function isValidFrameOrder(start: FrameBound, end: FrameBound): boolean {
  return frameBoundRank(end) >= frameBoundRank(start);
}

export { parseOverBody } from './overBodyParse';
export { serializeOverBody } from './overBodySerialize';
